import { useEffect, useState } from "react";

/**
 * A `Date` that updates on every wall-clock minute boundary.
 *
 * Sub-minute updates would thrash for no visible change, and
 * `setInterval(60_000)` is the wrong tool: background tabs throttle it
 * (sometimes to ≥10 minutes), so anything driven by it silently freezes —
 * a "now" marker would sit minutes behind when the tab comes back. Instead
 * each tick schedules the next one on the coming minute boundary, and
 * `visibilitychange` forces an immediate catch-up on return.
 *
 * `enabled` false stops the clock entirely (nothing is showing it), and
 * switching it back on re-syncs straight away.
 */
export function useMinuteTick(enabled = true): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!enabled) return;

    let timer: number | undefined;
    const tick = () => {
      const current = new Date();
      setNow(current);
      timer = window.setTimeout(tick, 60_000 - (current.getTime() % 60_000));
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (timer !== undefined) window.clearTimeout(timer);
      tick();
    };

    tick();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled]);

  return now;
}
